import { PaletteRange } from '@mui/material/styles/createPalette';
import { alpha, colorChannel, emphasize } from '@mui/system/colorManipulator';
import chroma from 'chroma-js';

import {
  defaultPrimary,
  error,
  info,
  neutral,
  stormGrey,
  success,
  warning,
} from './colors';

import type { ColorScheme } from './types';
import type { ColorSystemOptions } from '@mui/material/styles';

interface Config {
  primaryColor: string;
}

export function colorSchemes(
  config: Config
): Partial<Record<ColorScheme, ColorSystemOptions>> {
  const primary = createColorVariants(config.primaryColor);

  return {
    dark: {
      palette: {
        action: { disabledBackground: 'rgba(0, 0, 0, 0.12)' },
        background: {
          default: neutral[950],
          defaultChannel: '9 10 11',
          paper: 'var(--mui-palette-neutral-900)',
          level1: neutral[800],
          level1Channel: colorChannel(neutral[800]), //'32 36 39',
          level2: 'var(--mui-palette-neutral-700)',
          level2Channel: colorChannel(neutral[700]),
          level3: 'var(--mui-palette-neutral-600)',
          level3Channel: colorChannel(neutral[600]),
        },
        common: { black: '#000000', white: '#ffffff' },
        divider: 'var(--mui-palette-neutral-700)',
        dividerChannel: '50 56 62',
        error: {
          ...error,
          lightest: error[100],
          light: error[300],
          main: error[400],
          dark: error[500],
          darkest: error[900],
          contrastText: 'var(--mui-palette-common-black)',
          activated:
            'rgba(var(--mui-palette-error-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-error-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-error-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },
        info: {
          ...info,
          lightest: info[100],
          light: info[300],
          main: info[400],
          dark: info[500],
          darkest: info[900],
          contrastText: 'var(--mui-palette-common-black)',
          activated:
            'rgba(var(--mui-palette-info-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-info-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-info-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },
        neutral: {
          ...neutral,
          '50Channel': colorChannel(neutral[50]),
          '100Channel': colorChannel(neutral[100]),
          '200Channel': colorChannel(neutral[200]),
          '300Channel': colorChannel(neutral[300]),
          '400Channel': colorChannel(neutral[400]),
          '500Channel': colorChannel(neutral[500]),
          '600Channel': colorChannel(neutral[600]),
          '700Channel': colorChannel(neutral[700]),
          '800Channel': colorChannel(neutral[800]),
          '900Channel': colorChannel(neutral[900]),
          '950Channel': colorChannel(neutral[950]),
        } satisfies PaletteRange,
        primary: primary.dark,
        secondary: withAlphas({
          ...neutral,
          lightest: neutral[50],
          light: neutral[100],
          main: neutral[200],
          dark: neutral[300],
          darkest: neutral[600],
          contrastText: 'var(--mui-palette-common-black)',
          activated:
            'rgba(var(--mui-palette-secondary-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-secondary-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-secondary-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        }),
        success: {
          ...success,
          lightest: success[100],
          light: success[300],
          main: success[400],
          dark: success[500],
          darkest: success[900],
          contrastText: 'var(--mui-palette-common-black)',
          activated:
            'rgba(var(--mui-palette-success-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-success-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-success-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },
        alert: {
          ...defaultPrimary,
          lightest: defaultPrimary[100],
          lightestChannel: colorChannel(defaultPrimary[100]),
          light: defaultPrimary[400],
          lightChannel: colorChannel(defaultPrimary[400]),
          main: defaultPrimary[500],
          mainChannel: colorChannel(defaultPrimary[500]),
          dark: defaultPrimary[600],
          darkChannel: colorChannel(defaultPrimary[600]),
          darkest: defaultPrimary[900],
          darkestChannel: colorChannel(defaultPrimary[900]),
          contrastText: 'var(--mui-palette-common-black)',
          activated:
            'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },
        text: {
          primary: 'var(--mui-palette-neutral-100)',
          primaryChannel: '240 244 248',
          secondary: 'var(--mui-palette-neutral-400)',
          secondaryChannel: '159 166 173',
          disabled: 'var(--mui-palette-neutral-600)',
        },
        warning: {
          ...warning,
          lightest: warning[100],
          light: warning[300],
          main: warning[400],
          dark: warning[500],
          darkest: warning[900],
          contrastText: 'var(--mui-palette-common-black)',
          activated:
            'rgba(var(--mui-palette-warning-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-warning-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-warning-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },
        Avatar: { defaultBg: 'var(--mui-palette-neutral-500)' },
        Backdrop: { bg: 'rgba(0, 0, 0, 0.5)' },
        OutlinedInput: { border: 'var(--mui-palette-neutral-700)' },
        TableCell: { border: 'var(--mui-palette-divider)' },
        Tooltip: { bg: 'rgba(10, 13, 20, 0.75)' },
      },
    },
    light: {
      palette: {
        action: { disabledBackground: 'rgba(0, 0, 0, 0.06)' },
        background: {
          default: '#ffffff',
          defaultChannel: '255 255 255',
          paper: 'var(--mui-palette-common-white)',
          level1: stormGrey[50],
          level1Channel: colorChannel(stormGrey[50]),
          level2: 'var(--mui-palette-neutral-100)',
          level2Channel: colorChannel(stormGrey[100]),
          level3: 'var(--mui-palette-neutral-200)',
          level3Channel: colorChannel(stormGrey[200]),
        },
        common: { black: '#000000', white: '#ffffff' },
        divider: 'var(--mui-palette-neutral-200)',
        dividerChannel: '220 223 228',
        error: {
          ...error,
          lightest: error[100],
          light: error[400],
          main: error[500],
          dark: error[600],
          darkest: error[900],
          contrastText: 'var(--mui-palette-common-white)',
          activated:
            'rgba(var(--mui-palette-error-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-error-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-error-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },
        info: {
          ...info,
          lightest: info[100],
          light: info[400],
          main: info[500],
          dark: info[600],
          darkest: info[900],
          contrastText: 'var(--mui-palette-common-white)',
          activated:
            'rgba(var(--mui-palette-info-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-info-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-info-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },
        neutral: {
          ...stormGrey,
          '50Channel': colorChannel(stormGrey[50]),
          '100Channel': colorChannel(stormGrey[100]),
          '200Channel': colorChannel(stormGrey[200]),
          '300Channel': colorChannel(stormGrey[300]),
          '400Channel': colorChannel(stormGrey[400]),
          '500Channel': colorChannel(stormGrey[500]),
          '600Channel': colorChannel(stormGrey[600]),
          '700Channel': colorChannel(stormGrey[700]),
          '800Channel': colorChannel(stormGrey[800]),
          '900Channel': colorChannel(stormGrey[900]),
          '950Channel': colorChannel(stormGrey[950]),
        } satisfies PaletteRange,
        primary: primary.light,
        secondary: withAlphas({
          ...neutral,
          lightest: neutral[100],
          light: neutral[600],
          main: neutral[700],
          dark: neutral[800],
          darkest: neutral[900],
          contrastText: 'var(--mui-palette-common-white)',
          activated:
            'rgba(var(--mui-palette-secondary-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-secondary-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-secondary-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        }),
        success: {
          ...success,
          lightest: success[100],
          light: success[400],
          main: success[500],
          dark: success[600],
          darkest: success[900],
          contrastText: 'var(--mui-palette-common-white)',
          activated:
            'rgba(var(--mui-palette-success-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-success-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-success-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },
        alert: {
          ...defaultPrimary,
          lightest: defaultPrimary[100],
          light: defaultPrimary[400],
          main: defaultPrimary[500],
          dark: defaultPrimary[600],
          darkest: defaultPrimary[900],
          contrastText: 'var(--mui-palette-common-white)',
          activated:
            'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },

        text: {
          primary: 'var(--mui-palette-neutral-900)',
          primaryChannel: '33 38 54',
          secondary: 'var(--mui-palette-neutral-500)',
          secondaryChannel: '102 112 133',
          disabled: 'var(--mui-palette-neutral-400)',
        },
        warning: {
          ...warning,
          lightest: warning[100],
          light: warning[400],
          main: warning[500],
          dark: warning[600],
          darkest: warning[900],
          contrastText: 'var(--mui-palette-common-white)',
          activated:
            'rgba(var(--mui-palette-warning-mainChannel) / var(--mui-palette-action-activatedOpacity))',
          hovered:
            'rgba(var(--mui-palette-warning-mainChannel) / var(--mui-palette-action-hoverOpacity))',
          selected:
            'rgba(var(--mui-palette-warning-mainChannel) / var(--mui-palette-action-selectedOpacity))',
        },
        Avatar: { defaultBg: 'var(--mui-palette-neutral-400)' },
        Backdrop: { bg: 'rgb(18, 22, 33, 0.8)' },
        OutlinedInput: { border: 'var(--mui-palette-neutral-200)' },
        TableCell: { border: 'var(--mui-palette-divider)' },
        Tooltip: { bg: 'rgba(10, 13, 20, 0.75)' },
      },
    },
  };
}

const withAlphas = (color) => {
  return {
    ...color,
    alpha4: alpha(color.main, 0.04),
    alpha8: alpha(color.main, 0.08),
    alpha12: alpha(color.main, 0.12),
    alpha30: alpha(color.main, 0.3),
    alpha50: alpha(color.main, 0.5),
  };
};

export function mapNumberRange(
  current: number,
  in_min: number,
  in_max: number,
  out_min: number,
  out_max: number
): number {
  const mapped: number =
    ((current - in_min) * (out_max - out_min)) / (in_max - in_min) + out_min;
  return clamp(mapped, out_min, out_max);
}

const getGammaCorrection = (baseColor, mode) => {
  const baseLum = baseColor.lab()[0];
  const desiredLum = 50;
  const distance = desiredLum - baseLum;
  const acceptableRange = 15;
  const modifierForMode = 10;
  const cor = mapNumberRange(distance, -50, 50, 0.5, 2);
  if (mode === 'dark') {
    if (
      baseLum < desiredLum + acceptableRange + modifierForMode &&
      baseLum > desiredLum - acceptableRange
    ) {
      return 1;
    }
  }
  if (mode === 'light') {
    if (
      baseLum < desiredLum + acceptableRange &&
      baseLum > desiredLum - acceptableRange - modifierForMode
    ) {
      return 1;
    }
  }
  return cor;
};

const clamp = (number, min = 0, max = 1) => {
  return Math.max(min, Math.min(number, max));
};

const makeColors = (baseHexChroma, mode) => {
  const baseColor = baseHexChroma;
  const correction = getGammaCorrection(baseColor, mode);
  const fullyCorrect = correction <= 0.5 || correction >= 1.5;

  const whiteStop = baseHexChroma.set('hsl.l', 0.999).set('hsl.s', '*3');
  const blackStop = baseHexChroma.set('hsl.l', 0.09).set('hsl.s', '*3');

  const scale = chroma
    .scale([whiteStop, baseHexChroma, blackStop])
    .mode('lab')
    .gamma(correction)
    .correctLightness(fullyCorrect)
    .domain([0, 1000]);

  const colors = {
    50: scale(50).hex(),
    100: scale(100).hex(),
    200: scale(200).hex(),
    300: scale(300).hex(),
    400: scale(400).hex(),
    500: scale(500).hex(),
    600: scale(600).hex(),
    700: scale(700).hex(),
    800: scale(800).hex(),
    900: scale(900).hex(),
    950: scale(950).hex(),
  };

  let keyForBaseColor = 1000;
  let baseColorDistance = 10;
  Object.keys(colors).map((key) => {
    const color = chroma(colors[key]);
    const colorLum = color.luminance();
    const baseLum = baseColor.luminance();

    const distance = Math.abs(colorLum - baseLum);
    if (distance < baseColorDistance) {
      keyForBaseColor = Number(key);
      baseColorDistance = distance;
    }
  });
  colors[keyForBaseColor] = baseHexChroma.hex();

  return colors;
};

export const createColorVariants = (baseHex) => {
  const baseHexChroma = chroma(baseHex);
  const baseLum = baseHexChroma.lab()[0];
  const darkRange = makeColors(baseHexChroma, 'dark');
  const lightRange = makeColors(baseHexChroma, 'light');

  const lightOgSmart = baseLum < 50 ? baseHex : darkRange[500];
  const darkOgSmart = baseLum > 50 ? baseHex : darkRange[500];
  return {
    dark: withAlphas({
      ...darkRange,
      lightest: darkRange[100],
      light: darkRange[400],
      main: darkRange[500],
      dark: darkRange[600],
      darkest: darkRange[900],
      contrastText: emphasize(darkRange[500], 1),

      lightestChannel: colorChannel(darkRange[100]),
      lightChannel: colorChannel(darkRange[400]),
      mainChannel: colorChannel(darkRange[500]),
      darkChannel: colorChannel(darkRange[600]),
      darkestChannel: colorChannel(darkRange[900]),
      contrastTextChannel: colorChannel(emphasize(darkRange[500], 1)),

      og: baseHex,
      ogContrastText: emphasize(baseHex, 1),
      ogSmart: darkOgSmart,
      ogSmartContrastText: emphasize(darkOgSmart, 1),

      activated:
        'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-activatedOpacity))',
      hovered:
        'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-hoverOpacity))',
      selected:
        'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-selectedOpacity))',
    }),
    light: withAlphas({
      ...lightRange,
      lightest: lightRange[100],
      light: lightRange[400],
      main: lightRange[500],
      dark: lightRange[600],
      darkest: lightRange[900],
      contrastText: emphasize(lightRange[500], 1),

      lightestChannel: colorChannel(lightRange[100]),
      lightChannel: colorChannel(lightRange[400]),
      mainChannel: colorChannel(lightRange[500]),
      darkChannel: colorChannel(lightRange[600]),
      darkestChannel: colorChannel(lightRange[900]),
      contrastTextChannel: colorChannel(emphasize(lightRange[500], 1)),

      og: baseHex,
      ogContrastText: emphasize(baseHex, 1),
      ogSmart: lightOgSmart,
      ogSmartContrastText: emphasize(lightOgSmart, 1),

      activated:
        'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-activatedOpacity))',
      hovered:
        'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-hoverOpacity))',
      selected:
        'rgba(var(--mui-palette-primary-mainChannel) / var(--mui-palette-action-selectedOpacity))',
    }),
  };
};
